import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, ArrowRight } from "lucide-react";
import { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getFleetPerformance, RANGE_PRESETS, fmtPct, fmtPeso } from "../lib/analytics";
import UpgradeCard from "../components/UpgradeCard";

const SORT_OPTIONS = [
  { value: "utilization_desc", label: "Highest utilization" },
  { value: "utilization_asc", label: "Lowest utilization" },
  { value: "value_desc", label: "Highest booking value" },
  { value: "downtime_desc", label: "Highest downtime" },
];

/**
 * Phase 2J: fleet comparison. Surfaces "why is the Fortuner only 38%
 * utilized?" style questions; not a BI platform. Desktop gets a real
 * table; below `md:` it degrades to a sortable card list (spec X) rather
 * than a horizontally-scrolling table.
 */
export default function FleetPerformance() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [range, setRange] = useState("this_month");
  const [sort, setSort] = useState("utilization_desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planError, setPlanError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPlanError(null);
    getFleetPerformance({ range, sort })
      .then(setData)
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.error_code === "plan_upgrade_required") {
          setPlanError(detail);
        } else {
          toast.error(formatApiError(e));
        }
      })
      .finally(() => setLoading(false));
  }, [range, sort]);

  if (planError) {
    return (
      <UpgradeCard
        detail={planError} isOwner={isOwner}
        title="Unlock Fleet Performance"
        features={[
          "Compare utilization, idle days, and downtime across every vehicle",
          "Completed booking value + recorded expenses/income/net per vehicle",
          "Sort by highest/lowest utilization, booking value, or downtime",
        ]}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="fleet-performance-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Fleet</div>
        <h1 className="font-display text-3xl font-bold mt-1 inline-flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-[#1B4332]" /> Fleet Performance
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Compare utilization and recorded value across your fleet. Numbers reflect only what
          ConfirmPh has recorded: see each vehicle&apos;s Performance tab for the full definition.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1" data-testid="fp-range-picker">
          {RANGE_PRESETS.map((p) => (
            <button key={p.value} onClick={() => setRange(p.value)}
                    data-testid={`fp-range-${p.value}`}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                      range === p.value ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="fp-sort-select"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-10 text-center" data-testid="fp-loading">Loading fleet performance…</div>
      ) : !data || data.vehicles.length === 0 ? (
        <div className="text-sm text-slate-500 py-10 text-center rounded-xl border border-slate-200 bg-white" data-testid="fp-empty">
          No vehicles yet: add a vehicle to start seeing fleet performance.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="fp-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Utilization</th>
                  <th className="px-4 py-3">Rental days</th>
                  <th className="px-4 py-3">Idle days</th>
                  <th className="px-4 py-3">Downtime</th>
                  <th className="px-4 py-3">Completed booking value</th>
                  <th className="px-4 py-3">Recorded expenses</th>
                  <th className="px-4 py-3">Recorded net</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicles.map((v) => (
                  <tr key={v.vehicle_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/app/vehicles/${v.vehicle_id}?tab=performance`} className="font-medium text-slate-900 hover:underline">
                        {v.vehicle_name}
                      </Link>
                      <div className="text-xs text-slate-500">{v.vehicle_plate}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">{fmtPct(v.utilization_rate)}</td>
                    <td className="px-4 py-3">{v.rental_days}</td>
                    <td className="px-4 py-3">{v.idle_days}</td>
                    <td className="px-4 py-3">{v.maintenance_days + v.needs_inspection_days}</td>
                    <td className="px-4 py-3">{fmtPeso(v.completed_booking_value)}</td>
                    <td className="px-4 py-3">{fmtPeso(v.recorded_expenses)}</td>
                    <td className="px-4 py-3">{fmtPeso(v.recorded_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3" data-testid="fp-mobile-list">
            {data.vehicles.map((v) => (
              <Link key={v.vehicle_id} to={`/app/vehicles/${v.vehicle_id}?tab=performance`}
                    className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-300"
                    data-testid={`fp-card-${v.vehicle_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900">{v.vehicle_name}</div>
                    <div className="text-xs text-slate-500">{v.vehicle_plate}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-display font-bold text-[#1B4332]">{fmtPct(v.utilization_rate)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">utilized</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
                  <div>{v.rental_days} rental</div>
                  <div>{v.idle_days} idle</div>
                  <div>{v.maintenance_days + v.needs_inspection_days} downtime</div>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-600">Recorded net: <b>{fmtPeso(v.recorded_net)}</b></span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
